import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ChatIcon } from "@ui/react/icons";
import { EmptyState, PageHead } from "@ui/react";
import { ConversationList } from "./ConversationList";
import { MessageThread } from "./MessageThread";

/** The width at which both panes fit side by side, matching `.chat--split`. */
const SPLIT_QUERY = "(min-width: 1024px)";

function useIsSplit(): boolean {
  const [split, setSplit] = useState(() => window.matchMedia(SPLIT_QUERY).matches);

  useEffect(() => {
    const media = window.matchMedia(SPLIT_QUERY);
    const onChange = (event: MediaQueryListEvent) => setSplit(event.matches);
    media.addEventListener("change", onChange);
    // A resize between first paint and this effect would otherwise be missed.
    setSplit(media.matches);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return split;
}

/**
 * Serves both `/messages` and `/messages/:conversationId`.
 *
 * One route, two layouts: wide viewports show the inbox and the thread together,
 * narrow ones show whichever the URL implies — so a deep link into a
 * conversation behaves the same at every width, and the browser back button
 * lands on the inbox rather than the previous screen.
 */
export function MessagesScreen() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const split = useIsSplit();

  // The inbox is REST-backed and therefore not self-updating; the thread bumps
  // this whenever it does something that changes a conversation's last message.
  const [refreshToken, setRefreshToken] = useState(0);
  const refreshList = useCallback(() => setRefreshToken((value) => value + 1), []);

  const showList = split || !conversationId;
  const showThread = Boolean(conversationId);

  return (
    <>
      {/* The one h1 on this route. The thread's own header carries the JOB
          title as an h2 under it, which is the hierarchy that was missing when
          this screen had no heading at all. */}
      <PageHead
        title="Messages"
        lead={
          conversationId ? undefined : "One thread per job — questions, quotes and schedules."
        }
      />

    <div className={`chat${split ? " chat--split" : ""}`}>
      {showList ? (
        <ConversationList selectedId={conversationId} refreshToken={refreshToken} />
      ) : null}

      {showThread ? (
        <MessageThread
          // Remounting on id change resets the listener, scroll position and
          // read-receipt bookkeeping in one go, instead of leaking the previous
          // thread's state into the new one.
          key={conversationId}
          conversationId={conversationId as string}
          showBackLink={!split}
          onActivity={refreshList}
        />
      ) : split ? (
        <section className="chat__thread">
          <EmptyState title="Select a conversation" icon={<ChatIcon />}>
            Pick a job on the left to read the thread, send a quote or propose a time.
          </EmptyState>
        </section>
      ) : null}
    </div>
    </>
  );
}
